// Checklist progress, pure so the browser and the server say the same thing. The checklist is
// informational: nothing here, or anywhere that uses it, decides whether work may go ahead.

export interface ItemProgress {
  assigned: number;
  required: number;
  /** Enough photos are assigned. A PM may check an item whether or not this is true. */
  met: boolean;
  percent: number;
}

export function itemProgress(requiredCount: number, assignedCount: number): ItemProgress {
  const percent =
    requiredCount <= 0 ? 100 : Math.min(100, Math.round((assignedCount / requiredCount) * 100));
  return {
    assigned: assignedCount,
    required: requiredCount,
    met: assignedCount >= requiredCount,
    percent,
  };
}

export interface ChecklistSummary {
  albumCount: number;
  /** The album's name when there is exactly one, so the label never claims more than it covers. */
  albumName: string | null;
  itemsChecked: number;
  itemsTotal: number;
}

/** Items a PM has checked, over the albums that have a checklist. Null when none does. */
export function summarizeChecklists(
  albums: { albumName: string; items: { checked: boolean }[] }[],
): ChecklistSummary | null {
  const withItems = albums.filter((album) => album.items.length > 0);
  if (withItems.length === 0) return null;
  const items = withItems.flatMap((album) => album.items);
  const only = withItems.length === 1 ? withItems[0] : undefined;
  return {
    albumCount: withItems.length,
    albumName: only?.albumName ?? null,
    itemsChecked: items.filter((item) => item.checked).length,
    itemsTotal: items.length,
  };
}

export function progressLabel(summary: ChecklistSummary): string {
  const counts = `${summary.itemsChecked} of ${summary.itemsTotal} items checked`;
  return summary.albumName
    ? `${summary.albumName}: ${counts}`
    : `${counts} across ${summary.albumCount} albums`;
}
